import Dialog from '@mui/material/Dialog'
import Typography from '@mui/material/Typography'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faClockRotateLeft,
  faCirclePlus,
  faCircleInfo,
  faBan,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { LoadingStateComponent } from '@labrav/common'
import { FundingWalletHistoryEvent, FundingWalletProgram } from '../../types'
import { formatAmount } from '../../utils/format'
import { WalletBar, currencySymbol } from './WalletBar'
import { useStyles } from './WalletHistoryModal.styles'

const EVENT_META: Record<string, { title: string; icon: IconDefinition }> = {
  created: { title: 'Wallet created', icon: faCirclePlus },
  balance_changed: { title: 'Balance updated', icon: faClockRotateLeft },
  status_changed: { title: 'Status changed', icon: faCircleInfo },
  deactivated: { title: 'Wallet deactivated', icon: faBan },
}

const DEFAULT_META = { title: 'Wallet updated', icon: faClockRotateLeft }

const metaFor = (eventType: string) => EVENT_META[eventType] ?? DEFAULT_META

/** Which question the reader wants answered, so the caller can prompt for it. */
export type WalletHelpTopic = 'wallet' | 'shortfall'

interface WalletHistoryModalProps {
  open: boolean
  program: FundingWalletProgram | null
  events: FundingWalletHistoryEvent[]
  loading: boolean
  onClose: () => void
  onHelp: (topic: WalletHelpTopic) => void
}

export const WalletHistoryModal = ({
  open,
  program,
  events,
  loading,
  onClose,
  onHelp,
}: WalletHistoryModalProps) => {
  const { classes, cx } = useStyles()
  if (!program) return null
  const symbol = currencySymbol(program.currency)
  const hasShortfall = program.shortfall != null && program.shortfall > 0

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      classes={{ paper: classes.paper }}
      aria-labelledby="wallet-history-title"
      data-testid="wallet-history-modal"
    >
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.titleGroup}>
            <span className={classes.titleIcon}>
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </span>
            <Typography
              component="h2"
              id="wallet-history-title"
              className={classes.title}
            >
              {program.name} wallet history
            </Typography>
          </div>
          <span className={classes.headerDivider} />
          <button
            type="button"
            className={classes.help}
            data-testid="wallet-history-help"
            onClick={() => onHelp('wallet')}
          >
            <FontAwesomeIcon icon={faCircleInfo} />
            <Typography component="span" className={classes.helpText}>
              Help &amp; FAQs
            </Typography>
          </button>
        </div>
        <button
          type="button"
          className={classes.close}
          onClick={onClose}
          aria-label="Close"
          data-testid="wallet-history-close"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <div className={classes.body}>
        <WalletBar program={program} />

        {hasShortfall && (
          <div className={classes.warning}>
            <Typography className={classes.warningText}>
              Your funding requests exceed your wallet balance by{' '}
              <b>
                {symbol}
                {formatAmount(program.shortfall)}.
              </b>{' '}
              <button
                type="button"
                className={classes.warningLink}
                data-testid="wallet-shortfall-help"
                onClick={() => onHelp('shortfall')}
              >
                Learn here what you can do to resolve this issue.
              </button>
            </Typography>
          </div>
        )}

        {loading ? (
          <LoadingStateComponent
            heading="Fetching wallet history"
            description="It may take a few moments, please wait..."
          />
        ) : events.length === 0 ? (
          <Typography className={classes.empty}>
            No wallet updates available
          </Typography>
        ) : (
          <div className={classes.timeline}>
            {events.map((event, index) => {
              const meta = metaFor(event.eventType)
              const isLast = index === events.length - 1
              return (
                <div
                  key={event.id}
                  className={classes.event}
                  data-testid={`wallet-history-event-${event.id}`}
                >
                  <div className={classes.rail}>
                    <span className={classes.railIcon}>
                      <FontAwesomeIcon icon={meta.icon} />
                    </span>
                    {!isLast && <span className={classes.railLine} />}
                  </div>
                  <div className={classes.eventBody}>
                    <div className={classes.eventTitleRow}>
                      <Typography className={classes.eventTitle}>
                        {meta.title}
                      </Typography>
                      {event.delta && (
                        <span
                          className={cx(
                            classes.badge,
                            event.delta.sign === '+'
                              ? classes.badgePositive
                              : classes.badgeNegative
                          )}
                        >
                          <Typography
                            component="span"
                            className={classes.badgeText}
                          >
                            {event.delta.sign}
                            {symbol}
                            {formatAmount(event.delta.amount)}
                          </Typography>
                        </span>
                      )}
                    </div>
                    {event.summary && (
                      <Typography className={classes.eventSummary}>
                        {event.summary}
                      </Typography>
                    )}
                  </div>
                  {event.timestamp && (
                    <Typography className={classes.eventTime}>
                      {event.timestamp}
                    </Typography>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Dialog>
  )
}

export default WalletHistoryModal
